import * as React from 'react';
import { IDataService } from '../services/DataService';

const DataServiceContext = React.createContext<IDataService | undefined>(undefined);

export const DataServiceProvider: React.FC<{ service: IDataService; children: React.ReactNode }> = ({ service, children }) => (
    <DataServiceContext.Provider value={service}>{children}</DataServiceContext.Provider>
);

export function useDataService(): IDataService {
    const service = React.useContext(DataServiceContext);
    if (!service) {
        throw new Error('useDataService must be used inside <DataServiceProvider>.');
    }
    return service;
}

export interface IAsyncState<T> {
    data?: T;
    error?: string;
    loading: boolean;
    reload: () => void;
}

/**
 * Tiny loader hook: runs `loader` on mount and whenever `deps` change, keeps
 * the last good value while reloading, ignores results from stale calls.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: React.DependencyList): IAsyncState<T> {
    const [data, setData] = React.useState<T>();
    const [error, setError] = React.useState<string>();
    const [loading, setLoading] = React.useState(true);
    const [tick, setTick] = React.useState(0);
    const loaderRef = React.useRef(loader);
    loaderRef.current = loader;

    React.useEffect(() => {
        let active = true;
        setLoading(true);
        setError(undefined);
        loaderRef.current()
            .then((result) => {
                if (!active) return;
                setData(result);
            })
            .catch((e: unknown) => {
                if (!active) return;
                setError((e as Error)?.message ?? 'Не удалось загрузить данные.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, tick]);

    const reload = React.useCallback(() => setTick((t) => t + 1), []);
    return { data, error, loading, reload };
}
